import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, MapPin, Clock, Camera, AlertTriangle, Shield, ExternalLink, RefreshCw, Pencil, History } from 'lucide-react';
import { toast } from 'sonner';
import StatusChip from './StatusChip';
import { useTalentProfileModal } from './TalentProfileModalProvider';
import { formatDateTime12hInTz, timezoneLabel } from '../utils/timeFormat';
import { resolveProfilePhoto } from '../utils/profilePhoto';
import HaloRing from './HaloRing';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN = () => localStorage.getItem('token');

/**
 * Admin Attendance & Compliance panel for a shift.
 *
 * Shows per-talent:
 *  - Check-in & check-out selfies (thumbnails → full-screen lightbox)
 *  - Map pin with venue vs talent location (distance in meters)
 *  - Timestamps + hours worked
 *  - Override audit log + "Override Clock" action (requires reason)
 */
const AttendanceCompliancePanel = ({ shiftId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null); // selfie URL with auth
  const { open: openProfile } = useTalentProfileModal();

  // Override / Edit dialog shares the same manual-time form with a small
  // `mode` switch. `staff` + `kind` are always set; `existingTime` is the
  // existing timestamp when editing (so we can prefill the picker).
  const [formDialog, setFormDialog] = useState(null); // { mode:'override'|'edit', staff, kind, existingTime }
  const [formDate, setFormDate] = useState('');   // YYYY-MM-DD
  const [formTime, setFormTime] = useState('');   // HH:MM (24h internal)
  const [formReason, setFormReason] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Audit-history drawer
  const [historyFor, setHistoryFor] = useState(null); // booking_id
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/attendance/overview/${shiftId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const buildSelfieUrl = (u) => {
    if (!u) return null;
    if (u.startsWith('http')) return u;
    // /api/selfies/... served by backend — needs auth token as query
    return `${API}${u}${u.includes('?') ? '&' : '?'}auth=${encodeURIComponent(TOKEN() || '')}`;
  };

  const mapsUrl = (lat, lng, label = '') => {
    if (!lat || !lng) return null;
    return `https://www.google.com/maps?q=${lat},${lng}${label ? `(${encodeURIComponent(label)})` : ''}`;
  };

  // --- Shared date/time picker helpers -----------------------------------
  //
  // Iter-221 P0 timezone fix:
  //   Previously these helpers used the ADMIN'S BROWSER timezone (via
  //   `new Date(y, m-1, d, h, m).toISOString()`), which meant an admin
  //   in NY entering "10:00 AM" for an Arizona event would store the
  //   timestamp as 10:00 AM Eastern → ~14:00 UTC → displayed as ~7:00 AM
  //   Arizona time. The user wants the wall-clock entry interpreted in
  //   the EVENT'S timezone so payroll honors event-local time.
  //
  //   The helpers now take an explicit `tz` IANA string (e.g.
  //   "America/Phoenix"). When `tz` is falsy we fall back to the
  //   browser's local zone to preserve old behavior for events that
  //   don't have a timezone on file yet.

  const pad = (n) => String(n).padStart(2, '0');

  /** Format a UTC moment as YYYY-MM-DD + HH:MM (24h) wall-clock in `tz`. */
  const splitLocal = (iso, tz) => {
    if (!iso) return ['', ''];
    const d = new Date(iso);
    if (!tz) {
      return [
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      ];
    }
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(d).map(p => [p.type, p.value])
      );
      // Some browsers emit hour='24' instead of '00' at midnight.
      const hh = parts.hour === '24' ? '00' : parts.hour;
      return [`${parts.year}-${parts.month}-${parts.day}`, `${hh}:${parts.minute}`];
    } catch {
      // Bad TZ string — fall back to browser-local.
      return [
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      ];
    }
  };

  /**
   * Interpret a YYYY-MM-DD + HH:MM wall-clock as a moment in `tz` and
   * return an ISO 8601 UTC string. Algorithm:
   *   1. Compute what UTC moment X would be IF those numbers were UTC.
   *   2. Ask: what wall-clock would X show in `tz`?
   *   3. The difference between the two gives the tz offset for that
   *      wall-clock, and we subtract it to land on the real UTC.
   *
   * Handles DST boundaries because step 2 uses Intl.DateTimeFormat
   * which respects DST per-moment.
   */
  const combineLocalToISO = (dateStr, timeStr, tz) => {
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    if (!tz) {
      const local = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
      return local.toISOString();
    }
    try {
      const asUtc = Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0);
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        }).formatToParts(new Date(asUtc)).map(p => [p.type, p.value])
      );
      const tzWall = Date.UTC(
        +parts.year, +parts.month - 1, +parts.day,
        parts.hour === '24' ? 0 : +parts.hour,
        +parts.minute, +parts.second,
      );
      // tzWall - asUtc = offset of `tz` from UTC at that moment.
      const offsetMs = tzWall - asUtc;
      return new Date(asUtc - offsetMs).toISOString();
    } catch {
      const local = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
      return local.toISOString();
    }
  };

  const openOverrideDialog = (staff, kind) => {
    // Pre-fill with "now" so admins can just hit Apply for the common case.
    // Iter-221 P0 — "now" must be shown in the EVENT's timezone, not the
    // admin's, so the prefilled wall-clock matches the venue's clock.
    const tz = data?.shift?.timezone;
    const now = new Date();
    let dateStr; let timeStr;
    if (tz) {
      const [d, t] = splitLocal(now.toISOString(), tz);
      dateStr = d; timeStr = t;
    } else {
      dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    }
    setFormDialog({ mode: 'override', staff, kind, existingTime: null });
    setFormDate(dateStr);
    setFormTime(timeStr);
    setFormReason('');
    setFormNotes('');
  };

  const openEditDialog = (staff, kind) => {
    const existingTime = kind === 'in' ? staff.checkin_time : staff.checkout_time;
    const [d, t] = splitLocal(existingTime, data?.shift?.timezone);
    setFormDialog({ mode: 'edit', staff, kind, existingTime });
    setFormDate(d);
    setFormTime(t);
    setFormReason('');
    setFormNotes('');
  };

  // Iter-213 P0 — Inline admin quick-actions for GPS-only / selfie-failed
  // bookings. Both call new backend endpoints that update the booking's
  // review state and persist a reason in the audit log.
  const approveGpsOnly = async (s) => {
    const reason = window.prompt(
      'Note for the audit log (optional, e.g. "Talent confirmed onsite at supervisor stand-up"):'
    );
    try {
      await api.post(`/attendance/clock-in-reviews/${s.booking_id}/approve-gps-only`, { reason: reason || '' });
      toast.success('GPS-only check-in approved');
      fetchOverview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    }
  };

  const requestSelfieCorrection = async (s) => {
    const reason = window.prompt(
      'Why does this booking need a corrected selfie? (Sent to the talent as a notification.)',
      'Please retake your check-in selfie.'
    );
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.post(`/attendance/clock-in-reviews/${s.booking_id}/request-selfie-correction`, { reason: reason.trim() });
      toast.success('Selfie correction requested — talent notified');
      fetchOverview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to request correction');
    }
  };

  const closeFormDialog = () => {
    setFormDialog(null);
    setFormDate(''); setFormTime('');
    setFormReason(''); setFormNotes('');
  };

  const submitForm = async () => {
    if (!formDialog) return;
    const trimmedReason = (formReason || '').trim();
    if (!trimmedReason) {
      toast.error('Reason is required');
      return;
    }
    if (!formDate || !formTime) {
      toast.error('Please pick a date and time');
      return;
    }
    const isoTs = combineLocalToISO(formDate, formTime, data?.shift?.timezone);
    if (!isoTs) {
      toast.error('Invalid date or time');
      return;
    }

    // Client-side chronological validation mirrors the backend.
    const { staff, kind, mode } = formDialog;
    const pickedMs = new Date(isoTs).getTime();
    if (kind === 'out' && staff.checkin_time && pickedMs <= new Date(staff.checkin_time).getTime()) {
      toast.error('Clock-out must be after clock-in');
      return;
    }
    if (kind === 'in' && staff.checkout_time && pickedMs >= new Date(staff.checkout_time).getTime()) {
      toast.error('Clock-in must be before clock-out');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'edit') {
        await api.patch(`/attendance/bookings/${staff.booking_id}/clock-time`, {
          kind,
          timestamp: isoTs,
          reason: trimmedReason,
          notes: formNotes.trim() || null,
        });
        toast.success(`Clock-${kind} time updated`);
      } else if (kind === 'in') {
        await api.post('/attendance/override-checkin', null, {
          params: {
            booking_id: staff.booking_id,
            action: staff.checkin_time ? 'approve' : 'manual_checkin',
            reason: trimmedReason,
            notes: formNotes.trim() || undefined,
            timestamp: isoTs,
          },
        });
        toast.success('Clock-in override applied');
      } else {
        await api.post('/attendance/override-checkout', null, {
          params: {
            booking_id: staff.booking_id,
            reason: trimmedReason,
            notes: formNotes.trim() || undefined,
            timestamp: isoTs,
          },
        });
        toast.success('Clock-out override applied');
      }
      closeFormDialog();
      fetchOverview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to save ${kind === 'in' ? 'clock-in' : 'clock-out'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const openHistory = async (bookingId) => {
    setHistoryFor(bookingId);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/attendance/bookings/${bookingId}/override-history`);
      setHistoryRows(res.data.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { shift, staff = [], attendance_summary } = data;
  // Iter-213+ — Render check-in / check-out / override times in the VENUE
  // timezone so an admin/client in California viewing an Atlanta shift sees
  // the actual wall-clock times the talent signed in. Falls back to browser
  // tz when the shift has no timezone set so legacy shifts don't blank out.
  const venueTz = shift?.timezone || null;
  const fmtVenue = (iso) => formatDateTime12hInTz(iso, venueTz);
  const fmtVenueFull = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
    if (venueTz) opts.timeZone = venueTz;
    try { return d.toLocaleString('en-US', opts); } catch { return d.toLocaleString(); }
  };

  // Iter-213+ — Determine whether the shift is "active right now" for halo
  // computation. Ops semantics: confirmed talent without a check-in on an
  // active shift renders RED (not green). We consider a shift active from
  // its start_time (in venue TZ) until ~12h after end_time.
  const isShiftActive = (() => {
    if (!shift?.shift_date) return false;
    try {
      const dateStr = String(shift.shift_date).slice(0, 10);
      const start = new Date(`${dateStr}T${shift.start_time || '00:00:00'}`);
      const end = new Date(`${dateStr}T${shift.end_time || '23:59:59'}`);
      const now = new Date();
      // Window: from shift start through 12h after end (covers post-shift review).
      return now >= start && now <= new Date(end.getTime() + 12 * 60 * 60 * 1000);
    } catch {
      return false;
    }
  })();

  return (
    <Card className="bg-white border-gray-200" data-testid="attendance-compliance-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-cyan-600" />
            Attendance & Compliance
            {venueTz && (
              <span
                className="ml-2 text-[10px] uppercase tracking-wider text-gray-500 font-normal"
                data-testid="attendance-venue-tz-label"
              >
                · times in {timezoneLabel(venueTz)}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs" data-testid="attendance-staffed-badge">
              {attendance_summary.staffed_count ?? ((attendance_summary.checked_in || 0) + (attendance_summary.checked_out || 0))}/{attendance_summary.total} staffed
            </Badge>
            <Button variant="ghost" size="sm" onClick={fetchOverview} data-testid="refresh-compliance-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {staff.length === 0 && (
          <p className="text-sm text-gray-500 italic">No confirmed staff yet.</p>
        )}

        {staff.map((s) => {
          const inUrl = buildSelfieUrl(s.checkin_selfie_url);
          const outUrl = buildSelfieUrl(s.checkout_selfie_url);
          const venueLat = shift.venue_lat;
          const venueLng = shift.venue_lng;
          const dist = s.checkin_distance_meters;

          return (
            <div
              key={s.booking_id}
              className="border border-gray-200 rounded-lg p-3 space-y-3"
              data-testid={`compliance-row-${s.booking_id}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {(() => {
                    const halo = HaloRing.computeFromBooking(s, { shiftActive: isShiftActive });
                    const photoUrl = resolveProfilePhoto(s);
                    const photoButtonClass = photoUrl
                      ? 'w-9 h-9 rounded-full overflow-hidden shrink-0 hover:opacity-90 transition block'
                      : 'w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 shrink-0 hover:opacity-90 transition';
                    return (
                      <HaloRing state={halo.state} reason={halo.reason} size="md" testIdSuffix={`compliance-${s.booking_id}`}>
                        {photoUrl ? (
                          <button
                            type="button"
                            onClick={() => openProfile(s.talent_id || s.user_id)}
                            className={photoButtonClass}
                            data-testid={`compliance-photo-btn-${s.booking_id}`}
                            title="View talent profile"
                          >
                            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openProfile(s.talent_id || s.user_id)}
                            className={photoButtonClass}
                            data-testid={`compliance-photo-btn-${s.booking_id}`}
                            title="View talent profile"
                          >
                            {(s.first_name || '?').charAt(0)}{(s.last_name || '').charAt(0)}
                          </button>
                        )}
                      </HaloRing>
                    );
                  })()}
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => openProfile(s.talent_id || s.user_id)}
                      className="font-medium text-sm text-gray-900 truncate text-left hover:text-cyan-700 hover:underline block w-full"
                      title={`${s.first_name || ''} ${s.last_name || ''}`.trim()}
                    >
                      {s.first_name} {s.last_name}
                    </button>
                    <div className="text-xs text-gray-500 truncate">{s.role_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {s.needs_review && (
                    <Badge
                      className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100"
                      data-testid={`needs-review-badge-${s.booking_id}`}
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Needs Review
                    </Badge>
                  )}
                  <StatusChip status={s.attendance_status} />
                </div>
              </div>

              {/* GPS-only fallback strip — shown when selfie failed */}
              {(s.selfie_failed || s.checkout_selfie_failed) && (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                  data-testid={`gps-fallback-strip-${s.booking_id}`}
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <div className="font-medium">
                      GPS-only check-in {s.selfie_failed && s.checkout_selfie_failed ? '+ check-out' : (s.checkout_selfie_failed ? 'check-out' : '')} — selfie failed
                    </div>
                    {(s.device_info || s.checkout_device_info) && (
                      <div className="text-amber-800 break-all">
                        Device: {(s.device_info || s.checkout_device_info || '').slice(0, 140)}
                      </div>
                    )}
                    <div className="text-amber-800">
                      Method: <span className="font-mono">{s.checkin_method || 'manual'}</span>
                      {s.checkin_verification && <> · Status: <span className="font-mono">{s.checkin_verification}</span></>}
                    </div>
                  </div>
                </div>
              )}

              {/* Selfies row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Clock-In Selfie</div>
                  {inUrl ? (
                    <button
                      type="button"
                      onClick={() => setLightbox(inUrl)}
                      className="block w-full rounded border border-gray-200 overflow-hidden bg-gray-50"
                      data-testid={`checkin-selfie-${s.booking_id}`}
                    >
                      <img src={inUrl} alt="Check-in selfie" className="w-full h-24 object-cover" />
                    </button>
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center bg-gray-50 border border-dashed border-gray-200 rounded text-xs text-gray-400">
                      <Camera className="w-4 h-4 mr-1" /> None
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Clock-Out Selfie</div>
                  {outUrl ? (
                    <button
                      type="button"
                      onClick={() => setLightbox(outUrl)}
                      className="block w-full rounded border border-gray-200 overflow-hidden bg-gray-50"
                      data-testid={`checkout-selfie-${s.booking_id}`}
                    >
                      <img src={outUrl} alt="Check-out selfie" className="w-full h-24 object-cover" />
                    </button>
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center bg-gray-50 border border-dashed border-gray-200 rounded text-xs text-gray-400">
                      <Camera className="w-4 h-4 mr-1" /> None
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline + GPS */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-start gap-1">
                  <Clock className="w-3 h-3 text-green-600 mt-0.5" />
                  <div>
                    <div className="text-gray-500 flex items-center gap-1">
                      In
                      {s.checkin_time && (
                        <button
                          onClick={() => openEditDialog(s, 'in')}
                          className="ml-1 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-cyan-700"
                          title="Edit clock-in time"
                          data-testid={`edit-checkin-btn-${s.booking_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="text-gray-900 font-medium" data-testid={`checkin-time-${s.booking_id}`}>
                      {s.checkin_time ? fmtVenue(s.checkin_time) : '—'}
                    </div>
                    {s.checkin_lat && s.checkin_lng && (
                      <a
                        href={mapsUrl(s.checkin_lat, s.checkin_lng, 'Talent location')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline inline-flex items-center gap-1"
                        data-testid={`checkin-location-${s.booking_id}`}
                      >
                        <MapPin className="w-3 h-3" />
                        {dist != null ? `${Math.round(dist)}m from venue` : 'View pin'}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-1">
                  <Clock className="w-3 h-3 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-gray-500 flex items-center gap-1">
                      Out
                      {s.checkout_time && (
                        <button
                          onClick={() => openEditDialog(s, 'out')}
                          className="ml-1 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-cyan-700"
                          title="Edit clock-out time"
                          data-testid={`edit-checkout-btn-${s.booking_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="text-gray-900 font-medium" data-testid={`checkout-time-${s.booking_id}`}>
                      {s.checkout_time ? fmtVenue(s.checkout_time) : '—'}
                    </div>
                    {s.hours_worked != null && (
                      <div className="text-gray-500">{s.hours_worked.toFixed(2)}h worked</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Venue pin link (applies to all rows) */}
              {venueLat && venueLng && (
                <a
                  href={mapsUrl(venueLat, venueLng, shift.venue_name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-cyan-700"
                >
                  <MapPin className="w-3 h-3" />
                  Venue pin ({shift.venue_name})
                </a>
              )}

              {/* Override audit log */}
              {(s.checkin_override_reason || s.checkout_override_reason) && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 font-medium">
                      <AlertTriangle className="w-3 h-3" />
                      Override log
                    </div>
                    <button
                      onClick={() => openHistory(s.booking_id)}
                      className="text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                      data-testid={`view-history-btn-${s.booking_id}`}
                    >
                      <History className="w-3 h-3" /> Full history
                    </button>
                  </div>
                  {s.checkin_override_reason && (
                    <div data-testid={`override-log-in-${s.booking_id}`}>
                      <span className="font-medium">In:</span> {s.checkin_override_reason}
                      {s.checkin_override_at && ` — ${fmtVenueFull(s.checkin_override_at)}`}
                    </div>
                  )}
                  {s.checkout_override_reason && (
                    <div data-testid={`override-log-out-${s.booking_id}`}>
                      <span className="font-medium">Out:</span> {s.checkout_override_reason}
                      {s.checkout_override_at && ` — ${fmtVenueFull(s.checkout_override_at)}`}
                    </div>
                  )}
                </div>
              )}

              {/* Iter-213 P0 — Override + new GPS-review actions.
                  Mobile: buttons stack with flex-col. Desktop: row. */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2" data-testid={`compliance-actions-${s.booking_id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50 w-full sm:w-auto"
                  onClick={() => openOverrideDialog(s, 'in')}
                  data-testid={`override-checkin-btn-${s.booking_id}`}
                >
                  <Shield className="w-3 h-3 mr-1" />
                  Override Clock-In
                </Button>
                {s.checkin_time && !s.checkout_time && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50 w-full sm:w-auto"
                    onClick={() => openOverrideDialog(s, 'out')}
                    data-testid={`override-checkout-btn-${s.booking_id}`}
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    Override Clock-Out
                  </Button>
                )}
                {/* GPS-only / selfie-failed quick actions */}
                {(s.needs_review || s.selfie_failed || s.checkout_selfie_failed) && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto"
                      onClick={() => approveGpsOnly(s)}
                      data-testid={`approve-gps-only-btn-${s.booking_id}`}
                    >
                      <Shield className="w-3 h-3 mr-1" />
                      Approve GPS-only
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs border-rose-300 text-rose-700 hover:bg-rose-50 w-full sm:w-auto"
                      onClick={() => requestSelfieCorrection(s)}
                      data-testid={`request-selfie-correction-btn-${s.booking_id}`}
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      Request selfie correction
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>

      {/* Selfie lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-black p-0 border-0 max-w-2xl" data-testid="selfie-lightbox">
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          {lightbox && (
            <img src={lightbox} alt="Full selfie" className="w-full max-h-[80vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>

      {/* Override / Edit form dialog — shared by Override Clock-In/Out + Edit Time */}
      <Dialog open={!!formDialog} onOpenChange={(v) => !v && closeFormDialog()}>
        <DialogContent className="bg-white max-w-md" data-testid="attendance-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {formDialog?.mode === 'edit' ? 'Edit' : 'Override'} Clock-
              {formDialog?.kind === 'in' ? 'In' : 'Out'}
              {formDialog?.staff ? ` — ${formDialog.staff.first_name || ''} ${formDialog.staff.last_name || ''}`.trimEnd() : ''}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              {formDialog?.mode === 'edit'
                ? 'Correct a previously-recorded clock time. Every change is audit-logged.'
                : 'Manually record this clock event. Pick the exact date + time (not the current server time). Reason is required; a note is optional.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Iter-221 P0 — Make the event timezone visible right next to
                the time inputs so admins know their entry is interpreted
                in event-local time (not their browser-local time). */}
            {data?.shift?.timezone && (
              <div
                className="text-[11px] text-cyan-800 bg-cyan-50 border border-cyan-200 rounded p-2"
                data-testid="attendance-form-tz-hint"
              >
                Times are entered in <strong>{timezoneLabel(data.shift.timezone)}</strong>
                {' '}(event local time). What you type is what gets saved — no conversion to your local timezone.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="override-date" className="text-xs">Date</Label>
                <Input
                  id="override-date"
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  data-testid="override-date-input"
                />
              </div>
              <div>
                <Label htmlFor="override-time" className="text-xs">Time</Label>
                <Input
                  id="override-time"
                  type="time"
                  step="60"
                  value={formTime}
                  onChange={(e) => setFormTime(e.target.value)}
                  data-testid="override-time-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="override-reason" className="text-xs">Reason <span className="text-red-500">*</span></Label>
              <Textarea
                id="override-reason"
                value={formReason}
                onChange={(e) => setFormReason(e.target.value)}
                placeholder={
                  formDialog?.mode === 'edit'
                    ? 'e.g. Corrected after reviewing shift lead report'
                    : 'e.g. Talent forgot to clock out — confirmed attendance via shift lead'
                }
                rows={3}
                data-testid="override-reason-input"
              />
            </div>
            <div>
              <Label htmlFor="override-notes" className="text-xs">Note (optional)</Label>
              <Input
                id="override-notes"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Internal note, ticket #, etc."
                data-testid="override-notes-input"
              />
            </div>
            {formDialog?.existingTime && (
              <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
                Currently stored: <span className="font-medium">
                  {fmtVenueFull(formDialog.existingTime)}
                </span>
                {venueTz && <span className="ml-1 text-gray-400">({timezoneLabel(venueTz)})</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeFormDialog} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submitForm}
              disabled={submitting || !formReason.trim() || !formDate || !formTime}
              className={formDialog?.mode === 'edit'
                ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'}
              data-testid="override-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              {formDialog?.mode === 'edit' ? 'Save Changes' : 'Apply Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="bg-white max-w-xl" data-testid="attendance-history-dialog">
          <DialogHeader>
            <DialogTitle>Attendance change history</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Every manual override + edit on this booking. Auditable wage-hour record.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {historyLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></div>
            ) : historyRows.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No edits or overrides yet.</p>
            ) : historyRows.map((h) => (
              <div key={h.id} className="border border-gray-200 rounded p-2 text-xs" data-testid={`history-row-${h.id}`}>
                <div className="flex items-center gap-2">
                  <Badge className={h.action === 'edit' ? 'bg-cyan-100 text-cyan-700 border-0' : 'bg-amber-100 text-amber-700 border-0'}>
                    {h.action === 'edit' ? 'Edit' : 'Override'}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">Clock-{h.kind}</Badge>
                  <span className="text-gray-500 ml-auto">{fmtVenueFull(h.changed_at)}</span>
                </div>
                <div className="mt-1 text-gray-800">
                  <span className="font-medium">By:</span> {h.changed_by_name || h.changed_by_email || '—'}
                </div>
                <div className="text-gray-700">
                  {h.previous_time && (
                    <span className="text-gray-500 line-through mr-2">
                      {fmtVenueFull(h.previous_time)}
                    </span>
                  )}
                  <span className="font-medium">{fmtVenueFull(h.new_time)}</span>
                </div>
                <div className="mt-1 text-gray-600 italic">"{h.reason}"</div>
                {h.notes && <div className="text-gray-500 text-[11px]">Note: {h.notes}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AttendanceCompliancePanel;
